import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n";
import { api } from "../lib/apiClient";
import { logError } from "../lib/logger";
import { Header } from "../components/Header";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { ArrowRight, ShieldCheck, Info, CheckCircle2, FileText } from "lucide-react";
import { useCountdown, CountdownBanner, NextSessionBanner } from "../components/Countdown";

const heroImg = "/hero-ulaval.jpg";

export default function Landing() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [opensAt, setOpensAt] = useState(null);
  const [closesAt, setClosesAt] = useState(null);
  const [nextSession, setNextSession] = useState({ date: null, label: "" });
  const cd = useCountdown(opensAt, closesAt);

  useEffect(() => {
    api.get("/settings/deposit").then((r) => {
      if (r.data.opens_at) setOpensAt(new Date(r.data.opens_at));
      if (r.data.closes_at) setClosesAt(new Date(r.data.closes_at));
      if (r.data.next_opens_at) setNextSession({ date: new Date(r.data.next_opens_at), label: r.data.next_label || "" });
    }).catch((e) => logError("Failed to fetch deposit settings:", e));
  }, []);

  const eligibility = ["welcome.elig1", "welcome.elig2", "welcome.elig3", "welcome.elig4"];
  const docs = ["welcome.doc1", "welcome.doc2"];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Bandeau */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Campus de l'Université Laval" className="w-full h-full object-cover object-[center_55%]" />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary/60 via-secondary/30 to-transparent" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 min-h-[340px] sm:min-h-[440px] flex flex-col items-start justify-center text-white">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-secondary/60 border border-white/25 rounded-full px-4 py-1.5 backdrop-blur-sm">
            <ShieldCheck className="w-3.5 h-3.5" /> {t("hero.badge")}
          </span>
          <h1 data-testid="welcome-title" className="mt-6 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight text-white [text-shadow:0_2px_6px_rgba(0,0,0,0.6),0_4px_24px_rgba(0,0,0,0.5)]">
            {t("welcome.h1")}
          </h1>
        </div>
      </section>

      {/* Contenu */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 -mt-10 relative">
        <div className="bg-card rounded-2xl shadow-sm border border-slate-200/80 p-6 sm:p-10 space-y-8">
          <CountdownBanner cd={cd} />
          {(cd.phase === "closed" || cd.phase === "unscheduled") && (
            <NextSessionBanner date={nextSession.date} label={nextSession.label} />
          )}
          <p className="text-base text-slate-700 leading-relaxed" data-testid="welcome-intro">
            {t("welcome.intro")}
          </p>

          {/* Bloc alerte */}
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-5 flex gap-4" data-testid="welcome-important">
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900 leading-relaxed">
              <span className="font-bold">{t("welcome.importantLabel")} : </span>
              {t("welcome.important")}
            </p>
          </div>

          {/* Conditions d'admissibilité */}
          <div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-primary">{t("welcome.eligTitle")}</h2>
            <p className="mt-2 text-sm text-slate-600">{t("welcome.eligIntro")}</p>
            <ul className="mt-4 space-y-3">
              {eligibility.map((k) => (
                <li key={k} className="flex gap-3 text-sm text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{t(k)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pièces justificatives */}
          <div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-primary">{t("welcome.docsTitle")}</h2>
            <p className="mt-2 text-sm text-slate-600">{t("welcome.docsIntro")}</p>
            <ol className="mt-4 space-y-3">
              {docs.map((k, i) => (
                <li key={k} className="flex gap-3 text-sm text-slate-700">
                  <span className="grid place-items-center w-6 h-6 rounded-md bg-primary/10 text-primary font-bold text-xs flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    {t(k)}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* Confirmation + bouton */}
          <div className="pt-4 border-t border-slate-200 flex flex-col items-center gap-5">
            <label
              htmlFor="welcome-confirm"
              className="flex items-start gap-3 max-w-2xl cursor-pointer rounded-xl bg-slate-50 border border-slate-200 p-4 hover:border-primary/40 transition-colors"
            >
              <Checkbox
                id="welcome-confirm"
                data-testid="welcome-confirm-checkbox"
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(!!v)}
                className="mt-0.5"
              />
              <span className="text-sm text-slate-700 leading-relaxed font-medium">{t("welcome.confirm")}</span>
            </label>

            <Button
              data-testid="start-application-btn"
              onClick={() => navigate("/demande")}
              disabled={!confirmed || !cd.open}
              className="bg-primary hover:bg-primary/90 text-white h-12 px-10 text-base font-semibold rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("welcome.cta")} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            {!cd.open && (
              <p className="text-sm font-medium text-slate-500" data-testid="deposit-not-open">
                {cd.phase === "closed"
                  ? t("countdown.ended")
                  : cd.phase === "upcoming"
                    ? `${t("countdown.opensOn")} ${cd.opensAt.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "long", timeStyle: "short" })}`
                    : t("countdown.unscheduled")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
